import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CreditCard, Plus, RefreshCw, Pencil, Trash2, Save, X } from 'lucide-react';
import {
  listMyBeneficiaries, createMyBeneficiary, updateMyBeneficiary, archiveMyBeneficiary,
} from '@/services/remittanceApi';

const CURRENCIES = ['CUP', 'MLC', 'USD', 'Bandec'];

const empty = { full_name: '', document_id: '', phone: '', bank_name: '', account_number: '', account_currency: 'CUP', country: 'CU', notes: '' };

const BeneficiaryForm = ({ open, onClose, onSubmit, initial = null }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm(initial ? { ...empty, ...initial } : empty); }, [open, initial]);

  const valid = form.full_name.trim().length >= 2;
  const submit = async () => {
    if (!valid) { toast.error('Nombre del beneficiario es obligatorio'); return; }
    setBusy(true);
    try {
      await onSubmit({ ...form });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-xl" data-testid="bene-form-dialog">
        <DialogHeader><DialogTitle>{isEdit ? 'Editar beneficiario' : 'Nuevo beneficiario'}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500">Nombre completo *</label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="bene-form-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500">Documento (CI / pasaporte)</label>
              <Input value={form.document_id} onChange={(e) => setForm({ ...form, document_id: e.target.value })} data-testid="bene-form-doc" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500">Teléfono</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="bene-form-phone" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500">País</label>
              <Input value={form.country} maxLength={2} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} data-testid="bene-form-country" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500">Banco</label>
              <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="BANDEC, BPA, Metropolitano…" data-testid="bene-form-bank-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500">Número de cuenta</label>
              <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} data-testid="bene-form-account" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500">Moneda de la cuenta</label>
              <Select value={form.account_currency} onValueChange={(v) => setForm({ ...form, account_currency: v })}>
                <SelectTrigger data-testid="bene-form-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Notas internas</label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="bene-form-notes" />
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="bene-form-cancel"><X className="w-4 h-4 mr-1" />Cancelar</Button>
          <Button onClick={submit} disabled={busy || !valid} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="bene-form-save"><Save className="w-4 h-4 mr-1" />{busy ? 'Guardando…' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const GestorCuentasBancariasPage = () => {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const bene = await listMyBeneficiaries(getAuthHeaders);
      setItems(bene.items || []);
    } catch (e) {
      toast.error('Error al cargar');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (payload) => {
    if (editing) {
      await updateMyBeneficiary(getAuthHeaders, editing.id, payload);
      toast.success('Beneficiario actualizado');
    } else {
      await createMyBeneficiary(getAuthHeaders, payload);
      toast.success('Beneficiario creado');
    }
    setEditing(null); setShowForm(false);
    await load();
  };

  const archive = async (b) => {
    if (!window.confirm(`¿Eliminar a "${b.full_name}"?`)) return;
    try {
      await archiveMyBeneficiary(getAuthHeaders, b.id);
      toast.success('Beneficiario eliminado');
      await load();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  return (
    <div className="space-y-6" data-testid="gestor-cuentas-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-amber-600" /> Cuentas Bancarias
          </h1>
          <p className="text-xs text-slate-500 mt-1">Beneficiarios y sus cuentas guardadas para reutilizar en remesas.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="cuentas-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refrescar</Button>
          <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="cuentas-new"><Plus className="w-4 h-4 mr-1" />Nuevo beneficiario</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-12"><div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card className="border-0 shadow-sm p-12 text-center text-slate-500" data-testid="cuentas-empty">
          <CreditCard className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p>Aún no tienes beneficiarios guardados.</p>
          <Button onClick={() => { setEditing(null); setShowForm(true); }} className="mt-3 bg-amber-500 hover:bg-amber-600 text-slate-900"><Plus className="w-4 h-4 mr-1" />Agregar el primero</Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((b) => (
            <Card key={b.id} className="border-0 shadow-sm p-4" data-testid={`bene-card-${b.id}`}>
              <p className="font-bold text-slate-900">{b.full_name}</p>
              <p className="text-xs text-slate-500">{b.document_id ? `Doc: ${b.document_id}` : '—'}{b.phone ? ` · ${b.phone}` : ''}</p>
              <div className="text-xs text-slate-600 mt-2 space-y-1">
                {b.bank_name && <p><b>Banco:</b> {b.bank_name}</p>}
                {b.account_number && <p><b>Cuenta:</b> <span className="font-mono">{b.account_number}</span></p>}
                {b.account_currency && <span className="inline-block px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px] font-bold mr-1">{b.account_currency}</span>}
                {b.country && <span className="inline-block px-1.5 py-0.5 bg-slate-100 rounded text-[10px]">{b.country}</span>}
              </div>
              {b.notes && <p className="text-[11px] text-slate-500 mt-2 italic">"{b.notes}"</p>}
              <div className="flex justify-end gap-2 border-t pt-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => { setEditing(b); setShowForm(true); }} data-testid={`bene-edit-${b.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => archive(b)} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`bene-archive-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <BeneficiaryForm open={showForm} onClose={() => { setShowForm(false); setEditing(null); }} onSubmit={submit} initial={editing} />
    </div>
  );
};

export default GestorCuentasBancariasPage;
